import type { HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";

import { Badge } from "./badge";

export type StatusBadgeTone =
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "danger";

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: StatusBadgeTone;
  dot?: boolean;
  children?: ReactNode;
}

const toneClasses: Record<StatusBadgeTone, string> = {
  neutral: "border-border-subtle bg-surface-secondary text-content-secondary",
  info: "border-border-accent/40 bg-primary/10 text-content-accent",
  success: "border-state-success/30 bg-state-successSoft text-state-success",
  warning: "border-state-warning/30 bg-state-warningSoft text-state-warning",
  danger: "border-state-danger/30 bg-state-dangerSoft text-state-danger",
};

const dotClasses: Record<StatusBadgeTone, string> = {
  neutral: "bg-content-muted",
  info: "bg-content-accent",
  success: "bg-state-success",
  warning: "bg-state-warning",
  danger: "bg-state-danger",
};

export const StatusBadge = ({
  tone = "neutral",
  dot = true,
  className,
  children,
  ...props
}: StatusBadgeProps) => (
  <Badge
    className={clsx(
      "gap-1.5 rounded-pill border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide shadow-none",
      toneClasses[tone],
      className,
    )}
    {...props}
  >
    {dot ? (
      <span
        aria-hidden="true"
        className={clsx("h-1.5 w-1.5 rounded-full", dotClasses[tone])}
      />
    ) : null}
    {children}
  </Badge>
);

export default StatusBadge;
